import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Card,
  CardProps,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { sortBy, reverse } from "lodash";

import {
  ExerciseTypeSet,
  JourneyAnchors,
  Units,
  UnitType,
} from "../../../constants";

import { Settings } from "lucide-react";

import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  XAxis,
  YAxis,
  Tooltip as RechartsTooltip,
  TooltipProps as RechartsTooltipProps,
} from "recharts";
import dayjs, { Dayjs } from "dayjs";
import weekday from "dayjs/plugin/weekday";
import timezone from "dayjs/plugin/timezone";
import weekOfYear from "dayjs/plugin/weekOfYear";
import {
  NameType,
  ValueType,
  // eslint-disable-next-line import/no-unresolved
} from "recharts/types/component/DefaultTooltipContent";
import { chunkKeepTail } from "../../../utils/chart";
import { unitSetLabelCoach } from "../../../utils/units";
import { colorSystem } from "../../../theme";
import ChartCard from "./ChartCard";
import { HighlightItemInfo } from "../components/HighlightItem";
import { NoActivityExerciseMessageBox } from "./NoActivityExerciseMessageBox";

dayjs.extend(weekday);
dayjs.extend(timezone);
dayjs.extend(weekOfYear);

export interface ExerciseChartInfo {
  date: string;
  volume: number;
  weight: {
    min: number;
    max: number;
  };
  reps: {
    min: number;
    max: number;
  };
}

export interface VolumeChartProps extends CardProps {
  units?: Units;
  type: ExerciseTypeSet;
  volumes: ExerciseChartInfo[];
  highlight: HighlightItemInfo;
}

export enum TimeUnit {
  WEEK = "week",
  MONTH = "month",
  YEAR = "year",
  ALLTIME = "alltime",
}

const AVERAGING_WINDOW_SIZE = 2; // Amount of data points on each side to take into averaging min/max
const AVERAGING_FN_SLOPE = 1 + AVERAGING_WINDOW_SIZE; // coefficient of slope of the averaging function, bigger coeff = more influence of distant points

const ChartDot = (color: string, r: number, showInterpolated?: boolean) => {
  return ({ cx, cy, payload }: { cx: number; cy: number; payload: any }) => {
    return (!showInterpolated && payload.interpolated) || !payload.y ? null : (
      <circle
        cx={cx}
        cy={cy}
        r={r}
        fill={color}
        stroke={colorSystem.white}
        strokeWidth={r / 2}
      />
    );
  };
};

const ChartTooltip = (themeColor: string) => {
  return ({
    active,
    payload,
    label,
  }: RechartsTooltipProps<ValueType, NameType>) => {
    if (active && payload && payload.length) {
      return (
        <Card sx={{ padding: 1 }}>
          <Typography variant="subtitle2">
            {dayjs(label).format("MMMM DD, YYYY")}{" "}
          </Typography>
          <Typography color={themeColor} variant="h5">
            {parseFloat((payload[0].value as number).toFixed(1))}{" "}
            {payload[0].unit}
          </Typography>
        </Card>
      );
    }

    return null;
  };
};

const averagingFn = (index: number) =>
  (AVERAGING_FN_SLOPE - Math.abs(index - AVERAGING_WINDOW_SIZE)) /
  (AVERAGING_FN_SLOPE * AVERAGING_FN_SLOPE);

interface Settings {
  pointsAveraging: boolean;
  alignToRange: boolean;
}

export function VolumeChart(props: VolumeChartProps) {
  const { volumes, units, type, highlight } = props;

  const theme = useTheme();
  const { breakpoints } = useTheme();

  const [activeButton, setActiveButton] = useState<TimeUnit>(TimeUnit.ALLTIME);
  const [navIndex, setNavIndex] = useState(0);

  const smUp = useMediaQuery(breakpoints.up("sm"));

  const formattedValue = useMemo(() => {
    return volumes;
  }, [volumes]);

  const sortedData = useMemo(
    () => reverse(sortBy(formattedValue, ({ date }) => date)),
    [formattedValue],
  );

  const unit = unitSetLabelCoach(units, type);

  const borderColor = theme.palette.primary.main;

  const data = useMemo(
    () =>
      sortBy(sortedData, ({ date }) => date).map(({ date, volume }) => ({
        x: date as unknown as Dayjs,
        y: volume,
      })),
    [sortedData],
  );

  const chartData = useMemo(() => {
    const unaveragedChunkSizes = {
      week: 7,
      month: 30,
      year: 365,
      alltime: Infinity,
    };

    return chunkKeepTail(data, unaveragedChunkSizes[activeButton]);
  }, [activeButton, data]);

  const averagingStartPoint =
    navIndex - AVERAGING_WINDOW_SIZE > 0 ? navIndex - AVERAGING_WINDOW_SIZE : 0;
  const averagingEndPoint =
    navIndex + AVERAGING_WINDOW_SIZE + 1 > chartData.length
      ? chartData.length
      : navIndex + AVERAGING_WINDOW_SIZE + 1;

  const averagingData = chartData.slice(averagingStartPoint, averagingEndPoint);

  const chartMax = React.useMemo(() => {
    if (!chartData[navIndex] || !averagingData) return 0;
    return (
      Math.ceil(
        (Math.max(
          averagingData
            .map(
              (page, index) =>
                Math.max(...page.map((point) => point.y || -Infinity)) *
                averagingFn(
                  index + AVERAGING_WINDOW_SIZE * 2 + 1 - averagingData.length,
                ),
            )
            .reduce((acc, item) => acc + item, 0),
          ...chartData[navIndex].map((point) => point.y || -Infinity),
        ) *
          1.1) /
          5 +
          Number.EPSILON,
      ) * 5
    );
  }, [chartData, navIndex]);

  const chartMin = React.useMemo(() => {
    if (!chartData[navIndex] || !averagingData) return 0;
    return (
      Math.floor(
        Math.min(
          averagingData
            .map(
              (page, index) =>
                Math.min(...page.map((point) => point.y || Infinity)) /
                averagingFn(
                  index + AVERAGING_WINDOW_SIZE * 2 + 1 - averagingData.length,
                ),
            )
            .reduce((acc, item) => acc + item, 0),
          ...chartData[navIndex].map((point) => point.y || Infinity),
        ) /
          1.1 /
          5 +
          Number.EPSILON,
      ) * 5
    );
  }, [chartData, navIndex]);

  const chartTicks = React.useMemo(() => {
    const tickCount = 7;
    const ticks = new Set();
    for (let i = 1; i <= tickCount; i++) {
      ticks.add(
        Math.round(
          ((Math.pow(i, 2) / Math.pow(tickCount, 2)) * (chartMax - chartMin) +
            chartMin) /
            5,
        ) * 5,
      );
    }
    return Array.from(ticks) as number[];
  }, [chartMax, chartMin]);

  useEffect(() => {
    setNavIndex(chartData.length - 1);
  }, [chartData.length, activeButton]);

  const volumeCard = (
    <ChartCard
      title="Volume overview"
      classes={{ card: JourneyAnchors.VOLUME_CHART }}
      highlight={highlight}
      info={`Volume chart displays the total volume lifted during each day.`}
    >
      <Box position="relative" width="100%" height={308}>
        <ResponsiveContainer
          style={{
            fontFamily: "Montserrat",
            fontWeight: "bold",
            fontSize: 12,
          }}
          width="100%"
          height="100%"
        >
          <AreaChart margin={{ right: 50, top: 10 }} data={chartData[navIndex]}>
            <CartesianGrid strokeWidth={1.5} strokeDasharray="3 3" />
            {/*TODO: Add X-axis ticks with year */}
            <XAxis
              strokeWidth={1.5}
              dataKey="x"
              minTickGap={15}
              xAxisId={0}
              tickMargin={10}
              interval="preserveStart"
              tickFormatter={(value) => dayjs(value).format("MMM D")}
              stroke={theme.palette.text.secondary}
              fill={theme.palette.text.secondary}
            />

            <YAxis
              domain={[chartMin, chartMax]}
              ticks={chartTicks}
              axisLine={false}
              tickLine={false}
              strokeWidth={1.5}
              yAxisId={0}
              stroke={theme.palette.text.secondary}
              fill={theme.palette.text.secondary}
            />

            <RechartsTooltip
              animationDuration={300}
              animationEasing="linear"
              cursor={activeButton !== "week"}
              content={ChartTooltip(borderColor)}
            />

            <Area
              type={"monotone"}
              dataKey="y"
              unit={unit}
              stroke={borderColor}
              fillOpacity={0.6}
              fill={borderColor}
              strokeWidth={2}
              animationDuration={300}
              activeDot={ChartDot(borderColor, 6)}
              dot={ChartDot(borderColor, 4)}
            />
          </AreaChart>
        </ResponsiveContainer>

        {(!chartData[navIndex] || chartData[navIndex].length === 0) && (
          <NoActivityExerciseMessageBox sx={{ pt: 13 }} />
        )}
      </Box>
    </ChartCard>
  );
  return <>{volumeCard}</>;
}
